import {useEffect, useState} from 'react';
import {Routes} from 'constants/index';
import {hasWindow} from 'utils/functions';

import {createRedirect} from './auth';

type RedirectParams = {
	redirectTo?: string
	default?: {
		path?: string
		current?: boolean
	}
}

type RedirectReturn = {
	to?: string
	source: 'input-explicit' | 'input-default' | 'search' | 'current'
}

export const getRedirectURL = (args: RedirectParams): RedirectReturn => {
	if (args.redirectTo) {
		return {
			to: args.redirectTo,
			source: 'input-explicit',
		};
	}

	if (hasWindow()) {
		const param = new URLSearchParams(location.search).get('redirect_url');
		if (param) {
			return {
				to: param,
				source: 'search',
			};
		} else if (args.default?.current) {
			return {
				to: location.pathname,
				source: 'current',
			};
		}
	}

	return {
		to: args.default?.path,
		source: 'input-default',
	};
};

export const useRedirectURL = (base: string): string => {
	const [url, setURL] = useState<string>(base);

	// Use a base URL until the page loads. At that time
	// read the current URL from the window object and update.
	//
	// Doing it this way allows us to use the SSR version of
	// the page and avoid hydration & rendering errors.
	useEffect(() => {
		const redirect = getRedirectURL({default: {current: true}});

		// Leading slash gets trimmed so a redirect back to the
		// home page ends up as no redirect at all
		//
		// Likewise, a redirect from/to the sign-in/sign-up page
		// AFTER logging in is VERY unintuitive and very easy to
		// trigger if you hit the sign-in link once to get to
		// the page and then again on accident while on the page
		if (redirect.source === 'current' && (
			redirect.to === '/' ||
			redirect.to === Routes.signIn ||
			redirect.to === Routes.signUp ||
			redirect.to === Routes.resetPassword
		)) {
			redirect.to = Routes.dashboard;
		}

		setURL(createRedirect(base, redirect.to));
	}, []);

	return url;
};
